import React, { useState, useEffect } from 'react';
import { saasData } from '../../lib/supabase';

const ClassAttendanceDetail = ({ 
  selectedClass, 
  selectedTenant, 
  attendanceDate, 
  attendancePeriod, 
  onBack,
  onAttendanceSaved 
}) => {
  const [classMembers, setClassMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [studentNotes, setStudentNotes] = useState({});

  // Attendance status options
  const attendanceOptions = [
    { value: 'present', label: '出席', color: '#10b981', bgColor: '#d1fae5' },
    { value: 'absent', label: '缺席', color: '#ef4444', bgColor: '#fee2e2' },
    { value: 'late', label: '遲到', color: '#f59e0b', bgColor: '#fef3c7' },
    { value: 'early_leave', label: '早退', color: '#8b5cf6', bgColor: '#ede9fe' },
    { value: 'out', label: '外出', color: '#06b6d4', bgColor: '#cffafe' }
  ];

  // Fetch class members when component mounts
  useEffect(() => {
    const fetchClassMembers = async () => {
      if (!selectedClass || !selectedTenant) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching class members with params:', { selectedTenant, classId: selectedClass.id });
        const { data, error } = await saasData.getClassMembers(selectedTenant, selectedClass.id);
        
        console.log('getClassMembers response:', { data, error });
        
        if (error) {
          console.error('Error fetching class members:', error);
          setError('Failed to load class members');
          return;
        }
        
        console.log('Setting class members:', data);
        setClassMembers(data);
        
        // Fetch existing attendance records for this class and date
        const { data: attendanceRecords, error: attendanceError } = await saasData.getExistingAttendanceRecords(
          selectedTenant,
          selectedClass.id,
          attendanceDate
        );
        
        if (attendanceError) {
          console.error('Error fetching attendance records:', attendanceError);
        }
        
        // Initialize attendance status for all students
        const initialStatus = {};
        data.forEach(member => {
          // Check if there's an existing attendance record for this student
          const existingRecord = attendanceRecords?.find(record => record.student_id === member.student_id);
          initialStatus[member.student_id] = existingRecord?.attendance_status || 'present'; // Use existing status or default to present
        });
        setAttendanceStatus(initialStatus);
        
      } catch (err) {
        console.error('Error in fetchClassMembers:', err);
        setError('Failed to load class members');
      } finally {
        setLoading(false);
      }
    };

    fetchClassMembers();
  }, [selectedClass, selectedTenant, attendanceDate]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceStatus(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleAvatarClick = (studentId, event) => {
    // Check if click is on the status indicator (top-right corner)
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // If click is in top-right corner (status indicator area)
    if (x > rect.width * 0.7 && y < rect.height * 0.3) {
      // Cycle through attendance statuses
      const currentStatus = attendanceStatus[studentId] || 'present';
      const currentIndex = attendanceOptions.findIndex(opt => opt.value === currentStatus);
      const nextIndex = (currentIndex + 1) % attendanceOptions.length;
      const nextStatus = attendanceOptions[nextIndex].value;
      handleAttendanceChange(studentId, nextStatus);
    } else {
      // Center click - open popup
      const student = classMembers.find(m => m.student_id === studentId);
      setSelectedStudent(student);
      setShowPopup(true);
    }
  };

  const handleSaveAttendance = async () => {
    try {
      console.log('Saving attendance:', attendanceStatus);
      console.log('Student notes:', studentNotes);
      
      // Prepare attendance data for saving
      const attendanceData = classMembers.map(member => ({
        student_id: member.student_id,
        status: attendanceStatus[member.student_id] || 'present',
        notes: studentNotes[member.student_id] || null
      }));
      
      // Save attendance records to database
      const { data, error } = await saasData.saveAttendanceRecords(
        selectedTenant,
        selectedClass.id,
        attendanceData,
        attendanceDate
      );
      
      if (error) {
        console.error('Error saving attendance:', error);
        alert('Failed to save attendance: ' + error);
        return;
      }
      
      console.log('Attendance saved successfully:', data);
      alert('Attendance saved successfully!');
      
      // Call the callback to refresh the parent component
      if (onAttendanceSaved) {
        onAttendanceSaved();
      }
      
    } catch (err) {
      console.error('Error in handleSaveAttendance:', err);
      alert('Failed to save attendance: ' + err.message);
    }
  };

  const getAttendanceStats = () => {
    const total = classMembers.length;
    const present = Object.values(attendanceStatus).filter(status => status === 'present').length;
    const absent = Object.values(attendanceStatus).filter(status => status === 'absent').length;
    const late = Object.values(attendanceStatus).filter(status => status === 'late').length;
    const earlyLeave = Object.values(attendanceStatus).filter(status => status === 'early_leave').length;
    const out = Object.values(attendanceStatus).filter(status => status === 'out').length;
    
    return { total, present, absent, late, earlyLeave, out };
  };

  const getStatusInfo = (status) => {
    return attendanceOptions.find(opt => opt.value === status) || attendanceOptions[0];
  };

  const generateAvatar = (name) => {
    const initials = name ? name.charAt(0) : '?';
    const colors = ['#4f46e5', '#7c3aed', '#dc2626', '#ea580c', '#d97706', '#059669', '#0d9488', '#0891b2', '#2563eb', '#7c2d12'];
    const color = colors[name ? name.charCodeAt(0) % colors.length : 0];
    return { initials, color };
  };

  const stats = getAttendanceStats();

  console.log('Rendering ClassAttendanceDetail:', {
    classMembers: classMembers,
    stats: stats,
    selectedClass: selectedClass,
    selectedTenant: selectedTenant,
    viewMode: viewMode
  });

  if (loading) {
    return (
      <div style={{backgroundColor: '#f5f5f5', minHeight: '100vh'}}>
        <div style={{
          backgroundColor: '#4a90e2',
          color: 'white',
          padding: '16px',
          textAlign: 'center',
          position: 'relative'
        }}>
          <button 
            onClick={onBack}
            style={{
              position: 'absolute',
              left: '16px',
              top: '16px',
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ←
          </button>
          <h1 style={{fontSize: '18px', fontWeight: '600', margin: 0}}>點名詳情</h1>
        </div>
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '16px'
        }}>
          Loading class members...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{backgroundColor: '#f5f5f5', minHeight: '100vh'}}>
        <div style={{
          backgroundColor: '#4a90e2',
          color: 'white',
          padding: '16px',
          textAlign: 'center',
          position: 'relative'
        }}>
          <button 
            onClick={onBack}
            style={{
              position: 'absolute',
              left: '16px',
              top: '16px',
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ←
          </button>
          <h1 style={{fontSize: '18px', fontWeight: '600', margin: 0}}>點名詳情</h1>
        </div>
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#ef4444',
          fontSize: '16px'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{backgroundColor: '#f5f5f5', minHeight: '100vh'}}>
      {/* Header */}
      <div style={{
        backgroundColor: '#4a90e2',
        color: 'white',
        padding: '16px',
        textAlign: 'center',
        position: 'relative'
      }}>
        <button 
          onClick={onBack}
          style={{
            position: 'absolute',
            left: '16px',
            top: '16px',
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          ←
        </button>
        <h1 style={{fontSize: '18px', fontWeight: '600', margin: 0}}>點名詳情</h1>
      </div>

      {/* Class Info */}
      <div style={{
        backgroundColor: 'white',
        padding: '16px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <h2 style={{fontSize: '20px', fontWeight: '600', margin: 0, color: '#1f2937'}}>
            {selectedClass?.name}
          </h2>
          <span style={{
            fontSize: '14px',
            color: '#6b7280',
            backgroundColor: '#f3f4f6',
            padding: '4px 8px',
            borderRadius: '4px'
          }}>
            {stats.total} 人
          </span>
        </div>
        <div style={{fontSize: '14px', color: '#6b7280'}}>
          {attendanceDate} • {attendancePeriod}
        </div>
      </div>

      {/* View Mode Toggle */}
      <div style={{
        backgroundColor: 'white',
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{fontSize: '14px', color: '#6b7280', fontWeight: '500'}}>
          檢視模式
        </div>
        <div style={{
          display: 'flex',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          padding: '2px'
        }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: viewMode === 'grid' ? '#4a90e2' : 'transparent',
              color: viewMode === 'grid' ? 'white' : '#6b7280'
            }}
          >
            📱 網格
          </button>
          <button
            onClick={() => setViewMode('list')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: viewMode === 'list' ? '#4a90e2' : 'transparent',
              color: viewMode === 'list' ? 'white' : '#6b7280'
            }}
          >
            📋 列表
          </button>
        </div>
      </div>

      {/* Attendance Stats */}
      <div style={{
        backgroundColor: 'white',
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap'
      }}>
        {attendanceOptions.map(option => (
          <div key={option.value} style={{
            flex: 1,
            minWidth: '60px',
            textAlign: 'center',
            padding: '8px',
            backgroundColor: option.bgColor,
            borderRadius: '6px',
            border: `1px solid ${option.color}`
          }}>
            <div style={{fontSize: '18px', fontWeight: '600', color: option.color}}>
              {stats[option.value] || 0}
            </div>
            <div style={{fontSize: '10px', color: option.color}}>{option.label}</div>
          </div>
        ))}
      </div>

      {/* Student List/Grid */}
      <div style={{backgroundColor: 'white', paddingBottom: '80px'}}>
        {viewMode === 'grid' ? (
          // Grid View
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '16px',
            padding: '16px'
          }}>
            {classMembers.map((member) => {
              const statusInfo = getStatusInfo(attendanceStatus[member.student_id] || 'present');
              const avatar = generateAvatar(member.saas_students?.name);
              
              return (
                <div key={member.student_id} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '12px',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}>
                  {/* Avatar with Status Indicator */}
                  <div
                    onClick={(e) => handleAvatarClick(member.student_id, e)}
                    style={{
                      position: 'relative',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      backgroundColor: avatar.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    {avatar.initials}
                    {/* Status Indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: statusInfo.color,
                      border: '2px solid white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: '600',
                      color: 'white'
                    }}>
                      {statusInfo.label.charAt(0)}
                    </div>
                  </div>
                  
                  {/* Student Name */}
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937',
                    textAlign: 'center',
                    marginBottom: '4px',
                    lineHeight: '1.2'
                  }}>
                    {member.saas_students?.name || 'Unknown'}
                  </div>
                  
                  {/* Student ID */}
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    textAlign: 'center'
                  }}>
                    {member.saas_students?.student_code || member.student_id}
                  </div>
                  
                  {/* Status Label */}
                  <div style={{
                    fontSize: '11px',
                    color: statusInfo.color,
                    fontWeight: '500',
                    marginTop: '4px'
                  }}>
                    {statusInfo.label}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // List View
          <div>
            <div style={{
              backgroundColor: '#06b6d4',
              color: 'white',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr 1fr 1fr',
              padding: '12px 16px',
              fontSize: '16px',
              fontWeight: '600',
              gap: '16px'
            }}>
              <div style={{width: '50px'}}>頭像</div>
              <div>學生</div>
              <div style={{textAlign: 'center'}}>學號</div>
              <div style={{textAlign: 'center'}}>狀態</div>
            </div>

            {classMembers.map((member, index) => {
              const statusInfo = getStatusInfo(attendanceStatus[member.student_id] || 'present');
              const avatar = generateAvatar(member.saas_students?.name);
              
              return (
                <div 
                  key={member.student_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr 1fr 1fr',
                    padding: '16px',
                    borderBottom: index < classMembers.length - 1 ? '1px solid #f3f4f6' : 'none',
                    backgroundColor: 'white',
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  {/* Avatar */}
                  <div
                    onClick={(e) => handleAvatarClick(member.student_id, e)}
                    style={{
                      position: 'relative',
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: avatar.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '20px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {avatar.initials}
                    {/* Status Indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: statusInfo.color,
                      border: '2px solid white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: '600',
                      color: 'white'
                    }}>
                      {statusInfo.label.charAt(0)}
                    </div>
                  </div>
                  
                  {/* Student Name */}
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    {member.saas_students?.name || 'Unknown'}
                  </div>
                  
                  {/* Student ID */}
                  <div style={{
                    textAlign: 'center',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    {member.saas_students?.student_code || member.student_id}
                  </div>
                  
                  {/* Status Dropdown */}
                  <div style={{textAlign: 'center'}}>
                    <select
                      value={attendanceStatus[member.student_id] || 'present'}
                      onChange={(e) => handleAttendanceChange(member.student_id, e.target.value)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        color: statusInfo.color,
                        fontWeight: '500'
                      }}
                    >
                      {attendanceOptions.map(option => (
                        <option key={option.value} value={option.value} style={{color: option.color}}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        padding: '16px',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <button
          onClick={handleSaveAttendance}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          儲存點名記錄
        </button>
      </div>

      {/* Student Detail Popup */}
      {showPopup && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: generateAvatar(selectedStudent.saas_students?.name).color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px',
                fontWeight: '600',
                marginRight: '16px'
              }}>
                {generateAvatar(selectedStudent.saas_students?.name).initials}
              </div>
              <div>
                <h3 style={{fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0', color: '#1f2937'}}>
                  {selectedStudent.saas_students?.name || 'Unknown'}
                </h3>
                <p style={{fontSize: '14px', color: '#6b7280', margin: 0}}>
                  {selectedStudent.saas_students?.student_code || selectedStudent.student_id}
                </p>
              </div>
            </div>

            {/* Attendance Status */}
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151'}}>
                出席狀態
              </label>
              <select
                value={attendanceStatus[selectedStudent.student_id] || 'present'}
                onChange={(e) => handleAttendanceChange(selectedStudent.student_id, e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white'
                }}
              >
                {attendanceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151'}}>
                備註
              </label>
              <textarea
                value={studentNotes[selectedStudent.student_id] || ''}
                onChange={(e) => setStudentNotes(prev => ({
                  ...prev,
                  [selectedStudent.student_id]: e.target.value
                }))}
                placeholder="輸入備註..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  minHeight: '80px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowPopup(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                取消
              </button>
              <button
                onClick={() => setShowPopup(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                確定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassAttendanceDetail;
